import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { imgUrl } from "@/lib/api";
import PublicNav from "@/components/PublicNav";
import InquiryForm from "@/components/InquiryForm";
import logo from "@/assets/logo.png";
import { MapPin, BedDouble, Bath, Users, Sparkles, Star, ArrowRight, Loader2, Mail } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1753194931766-211c9f563156?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxmbG9yaWRhJTIwdmFjYXRpb24lMjBob21lJTIwZXh0ZXJpb3IlMjBwb29sfGVufDB8fHx8MTc4MTc5MDU5MXww&ixlib=rb-4.1.0&q=85";
const FALLBACK = "https://images.pexels.com/photos/11400444/pexels-photo-11400444.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function PublicHome() {
  const [villas, setVillas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Magical Villas Orlando | Luxury Orlando Vacation Villas";
    api.get("/public/properties").then(({ data }) => setVillas(data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-[#FDFBF7] min-h-screen" data-testid="public-home">
      {/* Hero */}
      <section className="relative h-[88vh] min-h-[560px] flex items-center">
        <img src={HERO} alt="Magical Villas Orlando" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A365D] via-[#1A365D]/50 to-[#1A365D]/30" />
        <PublicNav />
        <div className="relative z-20 max-w-7xl mx-auto px-6 sm:px-10 w-full">
          <div className="max-w-2xl animate-in fade-in slide-in-from-bottom-6 duration-700">
            <p className="flex items-center gap-2 text-[#F2C14E] text-xs uppercase tracking-[0.25em] font-bold mb-5">
              <Sparkles size={16} /> Minutes from the magic
            </p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] font-medium text-white mb-6">
              Luxury villas for your Orlando escape.
            </h1>
            <p className="text-lg text-white/85 mb-9 leading-relaxed">
              Spacious private-pool homes near the theme parks — thoughtfully designed for families and friends to make magical memories.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#villas" data-testid="hero-browse-btn" className="bg-[#E07A5F] text-white hover:bg-[#D0654B] rounded-full px-8 py-3.5 font-medium transition-colors flex items-center gap-2">
                Browse Villas <ArrowRight size={18} />
              </a>
              <a href="#contact" className="bg-white/15 hover:bg-white/25 backdrop-blur-md border border-white/30 text-white rounded-full px-8 py-3.5 font-medium transition-colors">
                Plan My Stay
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Villas */}
      <section id="villas" className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#E07A5F] mb-3">Our Collection</p>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-medium text-[#111827]">Handpicked Orlando Villas</h2>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#1A365D]" size={30} /></div>
        ) : villas.length === 0 ? (
          <p className="text-center text-gray-400 py-16">New villas coming soon — check back shortly.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {villas.map((v, i) => (
              <Link
                key={v.id}
                to={`/villa/${v.id}`}
                data-testid={`public-villa-${v.id}`}
                className="group bg-white border border-black/5 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={imgUrl(v.images?.[0]) || FALLBACK} alt={v.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-1 text-[#F2C14E] mb-2">
                    {[...Array(5)].map((_, s) => <Star key={s} size={14} fill="currentColor" />)}
                  </div>
                  <h3 className="font-display text-2xl font-medium leading-tight">{v.name}</h3>
                  <p className="text-sm text-gray-500 flex items-center gap-1 mt-1"><MapPin size={14} /> {v.location}</p>
                  <div className="flex items-center gap-4 mt-4 text-sm text-gray-600">
                    <span className="flex items-center gap-1"><BedDouble size={16} /> {v.bedrooms}</span>
                    <span className="flex items-center gap-1"><Bath size={16} /> {v.bathrooms}</span>
                    <span className="flex items-center gap-1"><Users size={16} /> {v.max_guests}</span>
                  </div>
                  <div className="flex items-center justify-between mt-5 pt-5 border-t border-black/5">
                    <span><span className="font-display text-2xl text-[#1A365D]">${v.price_per_night}</span><span className="text-sm text-gray-400"> / night</span></span>
                    <span className="text-sm font-medium text-[#E07A5F] flex items-center gap-1 group-hover:gap-2 transition-all">View <ArrowRight size={15} /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Contact */}
      <section id="contact" className="bg-[#1A365D] py-24">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#F2C14E] mb-3">Plan Your Stay</p>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-medium text-white mb-5">Let's make it magical.</h2>
            <p className="text-white/80 leading-relaxed">
              Tell us about your trip and our concierge will craft the perfect villa stay. We'll send availability, pricing and a secure GoDaddy invoice to confirm.
            </p>
            <a href="mailto:christine@magicalvillasorlando.com" data-testid="contact-email" className="mt-6 inline-flex items-center gap-2 text-[#F2C14E] hover:text-white transition-colors font-medium">
              <Mail size={18} /> christine@magicalvillasorlando.com
            </a>
          </div>
          <div className="bg-white rounded-2xl p-8 shadow-xl">
            <InquiryForm />
          </div>
        </div>
      </section>

      <footer className="bg-[#142a49] text-white/60 py-10 text-center text-sm">
        <img src={logo} alt="Magical Villas Orlando" className="h-28 w-auto mx-auto mb-4" />
        © {new Date().getFullYear()} Magical Villas Orlando. All rights reserved.
      </footer>
    </div>
  );
}
